// apps/vendor/src/pages/settings/WarehousesPage.tsx
//
// R3-6 (2026-05-16) — vendor warehouses CRUD UI.

import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsLayout } from '@printway/ui';

import { csrfFetch } from '../../lib/api/csrfFetch';
import { NOTICE_CLASS, STATUS_PILL_CLASS } from '../../lib/ui/statusPill.js';

interface Warehouse {
  warehouse_id: string;
  name: string;
  address: string;
  country: string;
  is_active: boolean;
  priority: number;
  products_assigned: number;
}

interface ListResponse {
  ok: boolean;
  data?: { rows: Warehouse[] };
  error?: { code: string; message: string };
}

interface MutationResponse {
  ok: boolean;
  data?: Warehouse;
  error?: { code: string; message: string };
}

const COUNTRY_CODES = ['VN', 'US', 'SG', 'TH', 'JP', 'GB'] as const;

export function WarehousesPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const countryOpts = COUNTRY_CODES.map((code) => ({
    code,
    label: t(`warehouses.country${code}`),
  }));
  const [rows, setRows] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    address: '',
    country: 'VN',
    priority: 100,
  });

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await csrfFetch('/api/v1/vendor/warehouses', {
        credentials: 'include',
      });
      const j = (await res.json()) as ListResponse;
      if (!res.ok || !j.ok || !j.data) {
        setError(j.error?.message ?? `HTTP_${res.status}`);
        return;
      }
      setRows(j.data.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'NETWORK');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submitCreate = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    setCreating(true);
    setFormError(null);
    try {
      const res = await csrfFetch('/api/v1/vendor/warehouses', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          address: form.address.trim(),
          country: form.country,
          priority: form.priority,
        }),
      });
      const j = (await res.json()) as MutationResponse;
      if (!res.ok || !j.ok || !j.data) {
        setFormError(j.error?.message ?? `HTTP_${res.status}`);
        return;
      }
      setShowForm(false);
      setForm({ name: '', address: '', country: 'VN', priority: 100 });
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'NETWORK');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (w: Warehouse): Promise<void> => {
    try {
      await csrfFetch(`/api/v1/vendor/warehouses/${w.warehouse_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !w.is_active }),
      });
      await load();
    } catch {
      // Swallow — load() error path will surface details.
    }
  };

  const handleDelete = async (w: Warehouse): Promise<void> => {
    if (
      !window.confirm(t('warehouses.confirmDelete', { name: w.name }))
    ) {
      return;
    }
    try {
      const res = await csrfFetch(`/api/v1/vendor/warehouses/${w.warehouse_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.status === 204) {
        await load();
        return;
      }
      const j = (await res.json().catch(() => null)) as ListResponse | null;
      setError(j?.error?.message ?? `HTTP_${res.status}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'NETWORK');
    }
  };

  return (
    <SettingsLayout portal="vendor" activeTab="warehouses">
      <section className="space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-pw-text">{t('warehouses.title')}</h2>
            <p className="text-xs text-pw-text-muted">
              {t('warehouses.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowForm((prev) => !prev);
              setFormError(null);
            }}
            data-testid="warehouses-toggle-create"
            className="inline-flex h-9 items-center rounded-md bg-pw-primary px-4 text-sm font-semibold text-white hover:bg-pw-primary-strong"
          >
            {showForm ? t('warehouses.cancelButton') : t('warehouses.addButton')}
          </button>
        </header>

        {showForm ? (
          <form
            onSubmit={submitCreate}
            data-testid="warehouses-create-form"
            className="space-y-3 rounded-xl border border-pw-border bg-pw-bg p-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-sm font-medium text-pw-text">{t('warehouses.nameLabel')}</span>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  className="h-9 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text"
                />
              </label>
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-sm font-medium text-pw-text">{t('warehouses.addressLabel')}</span>
                <input
                  type="text"
                  required
                  value={form.address}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address: e.target.value }))
                  }
                  className="h-9 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-pw-text">{t('warehouses.countryLabel')}</span>
                <select
                  value={form.country}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, country: e.target.value }))
                  }
                  className="h-9 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text"
                >
                  {countryOpts.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-pw-text">
                  {t('warehouses.priorityLabel')}
                </span>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={form.priority}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      priority: Number.parseInt(e.target.value, 10) || 100,
                    }))
                  }
                  className="h-9 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text"
                />
              </label>
            </div>
            {formError ? (
              <p className={`rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.danger}`}>
                {formError}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={creating}
              data-testid="warehouses-submit-create"
              className="rounded-md bg-pw-primary px-4 py-1.5 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-50"
            >
              {creating ? t('warehouses.creating') : t('warehouses.createSubmit')}
            </button>
          </form>
        ) : null}

        {error ? (
          <p
            data-testid="warehouses-error"
            className={`rounded-md px-3 py-2 text-sm ${NOTICE_CLASS.danger}`}
          >
            {error}
          </p>
        ) : null}

        {loading && rows.length === 0 ? (
          <p className="text-sm text-pw-text-muted">{t('warehouses.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
            {t('warehouses.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-pw-border">
            <table className="w-full text-sm">
              <thead className="bg-pw-surface text-xs uppercase tracking-wider text-pw-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">{t('warehouses.tableName')}</th>
                  <th className="px-3 py-2 text-left">{t('warehouses.tableAddress')}</th>
                  <th className="px-3 py-2 text-left">{t('warehouses.tableCountry')}</th>
                  <th className="px-3 py-2 text-right">{t('warehouses.tablePriority')}</th>
                  <th className="px-3 py-2 text-right">{t('warehouses.tableProducts')}</th>
                  <th className="px-3 py-2 text-left">{t('warehouses.tableStatus')}</th>
                  <th className="px-3 py-2 text-right">{t('warehouses.tableActions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr
                    key={w.warehouse_id}
                    data-testid={`warehouses-row-${w.warehouse_id}`}
                    className="border-t border-pw-border"
                  >
                    <td className="px-3 py-2 font-medium text-pw-text">{w.name}</td>
                    <td className="px-3 py-2 text-pw-text-muted">{w.address}</td>
                    <td className="px-3 py-2 text-pw-text-muted">{w.country}</td>
                    <td className="px-3 py-2 text-right text-pw-text-muted">
                      {w.priority}
                    </td>
                    <td className="px-3 py-2 text-right text-pw-text-muted">
                      {w.products_assigned}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                          w.is_active ? STATUS_PILL_CLASS.success : STATUS_PILL_CLASS.done
                        }`}
                      >
                        {w.is_active ? t('warehouses.statusActive') : t('warehouses.statusPaused')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => void toggleActive(w)}
                          className="rounded-md border border-pw-border bg-pw-surface px-2 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
                        >
                          {w.is_active ? t('warehouses.actionPause') : t('warehouses.actionActivate')}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(w)}
                          className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200 dark:hover:bg-red-900/40"
                        >
                          {t('warehouses.actionDelete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SettingsLayout>
  );
}

export default WarehousesPage;
